import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  Tabs,
  Tab,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import axios from "axios";
import { base_url } from "../../config/config";

// TabPanel component for tab navigation
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kunden-tabpanel-${index}`}
      aria-labelledby={`kunden-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const KundenManagement = () => {
  // State-Management
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [currentKunde, setCurrentKunde] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Formular-State
  const [formData, setFormData] = useState({
    konto: "",
    bezeichnung: "",
    kurzbezeichnung: "",
    ustIdNummer: "",
    address: {
      strasse: "",
      plz: "",
      ort: "",
      address: "",
    },
    email: "",
    phoneNumber: "",
    bemerkungen: "", // Add this new field
  });

  // Kunden bei Komponenten-Mount laden
  useEffect(() => {
    kundenAbrufen();
  }, []);

  // Funktion zum Abrufen aller Kunden
  const kundenAbrufen = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${base_url}/v1/getKunden`);
      setKunden(response.data.data);
      setError(null);
    } catch (err) {
      setError("Fehler beim Laden der Kunden. Bitte versuchen Sie es erneut.");
      console.error("Fehler beim Laden der Kunden:", err);
    } finally {
      setLoading(false);
    }
  };

  // Tab-Wechsel behandeln
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Formular-Eingabeänderungen verarbeiten
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Datei-Eingabeänderung für Import verarbeiten
  const handleFileChange = (e) => {
    setImportFile(e.target.files[0]);
  };

  // Datei-Import-Übermittlung verarbeiten
  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) {
      setError("Bitte wählen Sie eine Datei zum Importieren aus");
      return;
    }

    const formData = new FormData();
    formData.append("file", importFile);

    setLoading(true);
    try {
      await axios.post(`${base_url}/v1/importKunden`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setSuccess("Kunden erfolgreich importiert!");
      setShowImportDialog(false);
      setImportFile(null);
      kundenAbrufen(); // Liste aktualisieren
    } catch (err) {
      setError(
        "Fehler beim Importieren der Kunden. Bitte überprüfen Sie Ihre Datei und versuchen Sie es erneut."
      );
      console.error("Import-Fehler:", err);
    } finally {
      setLoading(false);
    }
  };

  // Formular zum Hinzufügen eines Kunden übermitteln
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${base_url}/v1/addKunden`, {
        konto: formData.konto,
        bezeichnung: formData.bezeichnung,
        kurzbezeichnung: formData.kurzbezeichnung,
        ustIdNummer: formData.ustIdNummer,
        address: {
          strasse: formData.address.strasse,
          plz: formData.address.plz,
          ort: formData.address.ort,
          address: formData.address.address,
        },
        email: formData.email ? [formData.email] : [],
        phoneNumber: formData.phoneNumber ? [formData.phoneNumber] : [],
        bemerkungen: formData.bemerkungen, // Add this field
      });

      setSuccess("Kunde erfolgreich hinzugefügt!");
      setShowAddDialog(false);
      resetForm();
      kundenAbrufen();
    } catch (err) {
      setError(
        "Fehler beim Hinzufügen des Kunden. Bitte versuchen Sie es erneut."
      );
      console.error("Hinzufügen-Fehler:", err);
    } finally {
      setLoading(false);
    }
  };

  // Formular zum Bearbeiten eines Kunden übermitteln
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${base_url}/v1/updateKunden/${currentKunde._id}`, {
        konto: formData.konto,
        bezeichnung: formData.bezeichnung,
        kurzbezeichnung: formData.kurzbezeichnung,
        ustIdNummer: formData.ustIdNummer,
        address: [
          {
            strasse: formData.address.strasse,
            plz: formData.address.plz,
            Ort: formData.address.ort,
            address: formData.address.address,
          },
        ],
        email: formData.email ? [formData.email] : [],
        phoneNumber: formData.phoneNumber ? [formData.phoneNumber] : [],
        bemerkungen: formData.bemerkungen, // Add this field
      });

      setSuccess("Kunde erfolgreich aktualisiert!");
      setShowEditDialog(false);
      resetForm();
      kundenAbrufen();
    } catch (err) {
      setError(
        "Fehler beim Aktualisieren des Kunden. Bitte versuchen Sie es erneut."
      );
      console.error("Aktualisierungs-Fehler:", err);
    } finally {
      setLoading(false);
    }
  };

  // Kunden löschen
  const handleDelete = async () => {
    if (!currentKunde) return;

    setLoading(true);
    try {
      // Hinweis: Dies setzt voraus, dass Sie einen deleteKunden-Endpunkt haben
      // Sie müssen dies möglicherweise zu Ihrem Backend hinzufügen
      await axios.delete(`${base_url}/v1/deleteKunden/${currentKunde._id}`);

      setSuccess("Kunde erfolgreich gelöscht!");
      setShowDeleteDialog(false);
      kundenAbrufen(); // Liste aktualisieren
    } catch (err) {
      setError(
        "Fehler beim Löschen des Kunden. Bitte versuchen Sie es erneut."
      );
      console.error("Lösch-Fehler:", err);
    } finally {
      setLoading(false);
    }
  };

  // Formular-Daten zurücksetzen
  const resetForm = () => {
    setFormData({
      konto: "",
      bezeichnung: "",
      kurzbezeichnung: "",
      ustIdNummer: "",
      address: {
        strasse: "",
        plz: "",
        ort: "",
        address: "",
      },
      email: "",
      phoneNumber: "",
      bemerkungen: "", // Add this field
    });
  };

  // Aktuellen Kunden für die Bearbeitung einrichten
  const setupEditKunde = (kunde) => {
    setCurrentKunde(kunde);
    setFormData({
      konto: kunde.konto || "",
      bezeichnung: kunde.bezeichnung || "",
      kurzbezeichnung: kunde.kurzbezeichnung || "",
      ustIdNummer: kunde.ustIdNummer || "",
      address: {
        strasse:
          kunde.address && kunde.address[0]
            ? kunde.address[0].strasse || ""
            : "",
        plz:
          kunde.address && kunde.address[0] ? kunde.address[0].plz || "" : "",
        ort:
          kunde.address && kunde.address[0] ? kunde.address[0].Ort || "" : "",
        address:
          kunde.address && kunde.address[0]
            ? kunde.address[0].address || ""
            : "",
      },
      email: kunde.email && kunde.email.length > 0 ? kunde.email[0] : "",
      phoneNumber:
        kunde.phoneNumber && kunde.phoneNumber.length > 0
          ? kunde.phoneNumber[0]
          : "",
      bemerkungen: kunde.bemerkungen || "", // Add this field
    });
    setShowEditDialog(true);
  };

  // Aktuellen Kunden zum Anzeigen einrichten
  const setupViewKunde = (kunde) => {
    setCurrentKunde(kunde);
    setShowViewDialog(true);
  };

  // Aktuellen Kunden für die Löschbestätigung einrichten
  const setupDeleteKunde = (kunde) => {
    setCurrentKunde(kunde);
    setShowDeleteDialog(true);
  };

  // Kunden basierend auf dem Suchbegriff filtern
  const filteredKunden = kunden.filter(
    (kunde) =>
      kunde.bezeichnung?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      kunde.konto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      kunde.kurzbezeichnung?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fehlermeldung schließen
  const handleCloseError = () => {
    setError(null);
  };

  // Erfolgsmeldung schließen
  const handleCloseSuccess = () => {
    setSuccess(null);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Kundenverwaltung
      </Typography>

      {/* Benachrichtigungen */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>

      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Kundenverwaltung Tabs"
          >
            <Tab label="Kundenliste" id="kunden-tab-0" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
            >
              <Box sx={{ display: "flex" }}>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Kunden suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    resetForm();
                    setShowAddDialog(true);
                  }}
                  sx={{ mr: 2 }}
                >
                  Kunde hinzufügen
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<UploadIcon />}
                  onClick={() => setShowImportDialog(true)}
                >
                  Kunden importieren
                </Button>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table aria-label="Kundentabelle">
                  <TableHead>
                    <TableRow>
                      <TableCell>Konto</TableCell>
                      <TableCell>Bezeichnung</TableCell>
                      <TableCell>Kurzbezeichnung</TableCell>
                      <TableCell>USt-ID</TableCell>
                      <TableCell>Adresse</TableCell>
                      <TableCell>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredKunden.length > 0 ? (
                      filteredKunden.map((kunde) => (
                        <TableRow key={kunde._id}>
                          <TableCell>{kunde.konto}</TableCell>
                          <TableCell>{kunde.bezeichnung}</TableCell>
                          <TableCell>{kunde.kurzbezeichnung}</TableCell>
                          <TableCell>{kunde.ustIdNummer}</TableCell>
                          <TableCell>
                            {kunde.address && kunde.address[0] ? (
                              <>
                                {kunde.address[0].strasse},{" "}
                                {kunde.address[0].plz} {kunde.address[0].Ort}
                              </>
                            ) : (
                              "Keine Adresse"
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="info"
                              aria-label="anzeigen"
                              onClick={() => setupViewKunde(kunde)}
                              size="small"
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              color="primary"
                              aria-label="bearbeiten"
                              onClick={() => setupEditKunde(kunde)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              aria-label="löschen"
                              onClick={() => setupDeleteKunde(kunde)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {kunden.length === 0
                            ? "Keine Kunden gefunden"
                            : "Keine Kunden, die den Suchkriterien entsprechen"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </TabPanel>
      </Box>

      {/* Kunde hinzufügen Dialog */}
      <Dialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Neuen Kunden hinzufügen</DialogTitle>
        <form onSubmit={handleAddSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Konto"
                  name="konto"
                  value={formData.konto}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="USt-ID Nummer"
                  name="ustIdNummer"
                  value={formData.ustIdNummer}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bezeichnung"
                  name="bezeichnung"
                  value={formData.bezeichnung}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Kurzbezeichnung"
                  name="kurzbezeichnung"
                  value={formData.kurzbezeichnung}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefonnummer"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>

              {/* Add Bemerkungen field */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bemerkungen"
                  name="bemerkungen"
                  value={formData.bemerkungen}
                  onChange={handleInputChange}
                  margin="normal"
                  multiline
                  rows={3}
                  placeholder="Zusätzliche Bemerkungen zum Kunden..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Adresse
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Straße"
                  name="address.strasse"
                  value={formData.address.strasse}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Adresszusatz"
                  name="address.address"
                  value={formData.address.address}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PLZ"
                  name="address.plz"
                  value={formData.address.plz}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ort"
                  name="address.ort"
                  value={formData.address.ort}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Kontaktinformationen
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="E-Mail"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefonnummer"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddDialog(false)} color="inherit">
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Kunde speichern"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Kunde bearbeiten Dialog */}
      <Dialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Kunde bearbeiten</DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Konto"
                  name="konto"
                  value={formData.konto}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="USt-ID Nummer"
                  name="ustIdNummer"
                  value={formData.ustIdNummer}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bezeichnung"
                  name="bezeichnung"
                  value={formData.bezeichnung}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Kurzbezeichnung"
                  name="kurzbezeichnung"
                  value={formData.kurzbezeichnung}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefonnummer"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>

              {/* Add Bemerkungen field */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bemerkungen"
                  name="bemerkungen"
                  value={formData.bemerkungen}
                  onChange={handleInputChange}
                  margin="normal"
                  multiline
                  rows={3}
                  placeholder="Zusätzliche Bemerkungen zum Kunden..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Adresse
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Straße"
                  name="address.strasse"
                  value={formData.address.strasse}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Adresszusatz"
                  name="address.address"
                  value={formData.address.address}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PLZ"
                  name="address.plz"
                  value={formData.address.plz}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ort"
                  name="address.ort"
                  value={formData.address.ort}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Kontaktinformationen
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="E-Mail"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefonnummer"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditDialog(false)} color="inherit">
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Kunde aktualisieren"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Kunden importieren Dialog */}
      <Dialog
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
      >
        <DialogTitle>Kunden importieren</DialogTitle>
        <form onSubmit={handleImportSubmit}>
          <DialogContent>
            <DialogContentText>
              Wählen Sie eine Excel- oder CSV-Datei zum Importieren aus.
            </DialogContentText>
            <TextField
              fullWidth
              type="file"
              inputProps={{
                accept: ".csv,.xlsx,.xls",
              }}
              onChange={handleFileChange}
              required
              margin="normal"
            />
            <Typography variant="caption" color="textSecondary">
              Unterstützte Formate: CSV, Excel (.xlsx, .xls)
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowImportDialog(false)} color="inherit">
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Importieren"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Kundendetails anzeigen Dialog */}
      <Dialog
        open={showViewDialog}
        onClose={() => setShowViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Kundendetails</DialogTitle>
        <DialogContent>
          {currentKunde && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Grundinformationen
                    </Typography>
                    <Typography>
                      <strong>Konto:</strong> {currentKunde.konto}
                    </Typography>
                    <Typography>
                      <strong>Bezeichnung:</strong> {currentKunde.bezeichnung}
                    </Typography>
                    <Typography>
                      <strong>Kurzbezeichnung:</strong>{" "}
                      {currentKunde.kurzbezeichnung}
                    </Typography>
                    <Typography>
                      <strong>USt-ID Nummer:</strong> {currentKunde.ustIdNummer}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Kontaktinformationen
                    </Typography>
                    <Typography>
                      <strong>E-Mail:</strong>{" "}
                      {currentKunde.email && currentKunde.email.length > 0
                        ? currentKunde.email.join(", ")
                        : "N/A"}
                    </Typography>
                    <Typography>
                      <strong>Telefon:</strong>{" "}
                      {currentKunde.phoneNumber &&
                      currentKunde.phoneNumber.length > 0
                        ? currentKunde.phoneNumber.join(", ")
                        : "N/A"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Bemerkungen
                    </Typography>
                    <Typography>
                      {currentKunde.bemerkungen ||
                        "Keine Bemerkungen vorhanden"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Adressinformationen
                </Typography>
                {currentKunde.address &&
                  currentKunde.address.map((addr, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1">
                          Adresse {index + 1}
                        </Typography>
                        <Typography>
                          {addr.strasse && <span>{addr.strasse}, </span>}
                          {addr.address && <span>{addr.address}, </span>}
                          {addr.plz && <span>{addr.plz} </span>}
                          {addr.Ort && <span>{addr.Ort}</span>}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewDialog(false)} color="inherit">
            Schließen
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowViewDialog(false);
              setupEditKunde(currentKunde);
            }}
          >
            Bearbeiten
          </Button>
        </DialogActions>
      </Dialog>

      {/* Löschen Bestätigungsdialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Kunde löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie den Kunden{" "}
            <strong>{currentKunde?.bezeichnung}</strong> löschen möchten? Diese
            Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} color="inherit">
            Abbrechen
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Löschen"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default KundenManagement;
